import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

async function geocode(q) {
  if (!q) return null;
  const raw = String(q).trim();
  if (!raw) return null;
  // Build candidate queries: full string, plus postal-code / city fallbacks, all scoped to Spain.
  const candidates = [];
  const pushUnique = (v) => { if (v && !candidates.includes(v)) candidates.push(v); };
  pushUnique(raw);
  const cp = raw.match(/\b\d{5}\b/);
  if (cp) pushUnique(cp[0]);
  // strip a leading postal code to get the city/locality part
  const cityPart = raw.replace(/\b\d{5}\b/, "").replace(/[·,]+/g, " ").trim();
  if (cityPart) pushUnique(cityPart);

  for (const c of candidates) {
    const usePostal = /^\d{5}$/.test(c);
    const params = new URLSearchParams({ format: "json", limit: "1", countrycodes: "es" });
    if (usePostal) params.set("postalcode", c);
    else params.set("q", `${c}, España`);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
        headers: { "Accept-Language": "es" },
      });
      const d = await r.json();
      if (d && d[0]) return [parseFloat(d[0].lat), parseFloat(d[0].lon)];
    } catch { /* try next candidate */ }
  }
  return null;
}

export default function RouteMap({ origin, destination }) {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [o, d] = await Promise.all([geocode(origin), geocode(destination)]);
      if (cancelled || !ref.current) return;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      const center = o || d || [40.4168, -3.7038];
      const map = L.map(ref.current, { scrollWheelZoom: false }).setView(center, 5);
      mapRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19, attribution: "© OpenStreetMap",
      }).addTo(map);
      const icon = (color) => L.divIcon({
        className: "",
        html: `<div style="background:${color};width:16px;height:16px;border:3px solid white;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,.35)"></div>`,
        iconSize: [16, 16], iconAnchor: [8, 8],
      });
      if (o) L.marker(o, { icon: icon("#FF5A00") }).addTo(map).bindPopup(`Origen: ${origin}`);
      if (d) L.marker(d, { icon: icon("#1E3F98") }).addTo(map).bindPopup(`Destino: ${destination}`);
      if (o && d) {
        L.polyline([o, d], { color: "#FF5A00", weight: 3, dashArray: "8 6" }).addTo(map);
        map.fitBounds([o, d], { padding: [40, 40] });
      } else if (o || d) {
        map.setView(o || d, 11);
      }
      setTimeout(() => map.invalidateSize(), 250);
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [origin, destination]);

  return <div ref={ref} data-testid="route-map" style={{ height: 280, width: "100%" }} className="border border-border bg-muted" />;
}
